import _ from 'lodash';
import {
  AUTO_GENERATE_ACCOUNT_ID,
  CUSTOM_DIVISION_CODE_MAPPING,
  DIRECT_DIVISION_CODE_MAPPING,
  DYNAMIC_ACCOUNT_ID,
  SECOND_ACCOUNT_ID_TYPES,
  STATIC_ACCOUNT_ID,
} from '../../../../../store/constants/accountIdConfigTypes';
import { humanize } from '../../../../../utils/humanize';

const defaultAccountIdConfigInputText = 'Set account id';
const usingRetailerDivisionCode = 'Using retailer division code';
const usingCustomDivisionCodeMapping = 'Using custom division code mapping';
const hasMultipleAccountIdsText = 'Multiple account ids exist';

export const nonCopyToClipboardAccountIdLabels = [
  defaultAccountIdConfigInputText,
  usingRetailerDivisionCode,
  usingCustomDivisionCodeMapping,
  hasMultipleAccountIdsText,
];

export function getAccountIdInputText(accountIdConfig) {
  if (_.isEmpty(accountIdConfig)) return defaultAccountIdConfigInputText;

  try {
    if (accountIdConfig.type === STATIC_ACCOUNT_ID && accountIdConfig.value) {
      return accountIdConfig.value;
    }

    if (SECOND_ACCOUNT_ID_TYPES.includes(accountIdConfig.type)) {
      const accountIdConfigVal = Array.isArray(accountIdConfig.value) ? accountIdConfig.value : [accountIdConfig.value];
      return accountIdConfigVal.join(', ');
    }

    if (isOnlyUsingDirectDivisionCode(accountIdConfig)) return usingRetailerDivisionCode;
    if (isOnlyUsingCustomDivisionCode(accountIdConfig)) return usingCustomDivisionCodeMapping;
    if (hasOnlyOneAutoGeneratedAccountId(accountIdConfig)) return getFirstAutoGeneratedAccountId(accountIdConfig);
    if (hasOnlyOneDynamicId(accountIdConfig)) return getFirstDynamicId(accountIdConfig);
    if (hasMultipleAccountIds(accountIdConfig)) return hasMultipleAccountIdsText;

    return defaultAccountIdConfigInputText;
  } catch (error) {
    console.warn('Error getting account id input text', error);
    return defaultAccountIdConfigInputText;
  }
}

function getAccountIdConfigTooltipHtml(accountIdConfig = {}, warehouses = []) {
  try {
    return Object.entries(accountIdConfig.mapping).map(([warehouseId, mappingValue]) => {
      const warehouse = warehouses.find(({ id }) => String(id) === String(warehouseId));
      return `Warehouse: ${warehouse.name} is using a ${humanize(mappingValue.type)}\n`;
    });
  } catch (error) {
    console.warn('Error getting account id config tooltip html: ', error);
    return '';
  }
}

function hasMultipleAccountIds(accountIdConfig) {
  if (!accountIdConfig) return false;
  return accountIdConfig.type === DYNAMIC_ACCOUNT_ID && Object.values(accountIdConfig.mapping).length >= 1;
}
function hasOnlyOneDynamicId(accountIdConfig) {
  const isDynamic = accountIdConfig.type === DYNAMIC_ACCOUNT_ID;
  const hasOnlyOne = Object.keys(accountIdConfig.mapping).length === 1;
  return isDynamic && hasOnlyOne;
}

function getFirstDynamicId(accountIdConfig = {}) {
  return Object.values(accountIdConfig.mapping || {})[0]?.id;
}

function hasOnlyOneAutoGeneratedAccountId(accountIdConfig) {
  if (!accountIdConfig) return false;
  try {
    const isAutoGenerate = Object.values(accountIdConfig.mapping)[0]?.type === AUTO_GENERATE_ACCOUNT_ID;
    const hasAutoGenerateMapping = !_.isEmpty(accountIdConfig.autoGenerateMapping);
    return hasOnlyOneDynamicId(accountIdConfig) && isAutoGenerate && hasAutoGenerateMapping;
  } catch (error) {
    return false;
  }
}
function getFirstAutoGeneratedAccountId(accountIdConfig) {
  return Object.values(accountIdConfig.autoGenerateMapping)[0]?.id;
}

function isOnlyUsingDirectDivisionCode(accountIdConfig) {
  if (accountIdConfig.type !== DYNAMIC_ACCOUNT_ID) return false;

  return Object.values(accountIdConfig.mapping).every(
    (mapping) => mapping?.type === DIRECT_DIVISION_CODE_MAPPING && !!mapping?.value?.enabled,
  );
}

function isOnlyUsingCustomDivisionCode(accountIdConfig) {
  if (accountIdConfig.type !== DYNAMIC_ACCOUNT_ID) return false;

  return Object.values(accountIdConfig.mapping).every((mapping) => mapping?.type === CUSTOM_DIVISION_CODE_MAPPING);
}

export const accountIdConfigTooltip = {
  htmlContent: getAccountIdConfigTooltipHtml,
  shouldShow: hasMultipleAccountIds,
};
